import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Sunrise, Sun, Sunset, Moon } from 'lucide-react';

/** Returns the greeting state for the current hour in Asia/Karachi.
 *  Morning 05:00–11:59, Afternoon 12:00–16:59, Evening 17:00–20:59, Night 21:00–04:59. */
export function pktGreeting() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Karachi', hour: 'numeric', hour12: false,
  }).formatToParts(new Date());
  const hour = Number(parts.find((p) => p.type === 'hour')?.value || 0);
  if (hour >= 5 && hour < 12) return { text: 'Good Morning', icon: Sunrise, tone: 'text-amber-400' };
  if (hour >= 12 && hour < 17) return { text: 'Good Afternoon', icon: Sun, tone: 'text-yellow-300' };
  if (hour >= 17 && hour < 21) return { text: 'Good Evening', icon: Sunset, tone: 'text-orange-400' };
  return { text: 'Good Night', icon: Moon, tone: 'text-indigo-300' };
}

/** Time-aware greeting banner used on every role dashboard.
 *  Props: subtitle (string), fallbackName (used if AuthContext user isn't ready),
 *  extra (React node — rendered inline next to the greeting, e.g. a UserBadge). */
export default function GreetingHeader({ subtitle, fallbackName = 'User', testId = 'greeting', extra = null }) {
  const { user } = useAuth();
  const [g, setG] = useState(pktGreeting());
  useEffect(() => {
    const t = setInterval(() => setG(pktGreeting()), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);
  const Icon = g.icon;
  return (
    <div>
      <div className="flex items-center gap-3 mb-1 flex-wrap">
        <Icon className={`h-8 w-8 ${g.tone}`} data-testid={`${testId}-icon`} />
        <h1 className="text-3xl font-bold text-white" data-testid={testId}>
          {g.text}, {user?.name || fallbackName}!
        </h1>
        {extra}
      </div>
      {subtitle && <p className="text-slate-400" data-testid={`${testId}-subtitle`}>{subtitle}</p>}
    </div>
  );
}
